/**
 * JavaScript XGBoost Skipping Predictor
 *
 * Feature extraction for skipping (jump rope) detection using pose keypoints.
 * Mirrors the Python implementation in ml/xgboost-counter/lib/xgboost_counter/strategies/skipping.py
 *
 * Uses POSITION-INVARIANT delta features to make the model robust to different
 * camera positions and body placements:
 * - Position features (ankle_y, hip_y, shoulder_y, body_center_y) are converted
 *   to deltas from the window baseline (mean across temporal window)
 * - Velocity features (ankle_vy, hip_vy) and body_height remain unchanged
 */

import { XGBoostPredictor } from './xgboostPredictor.js';

// Temporal window for skipping (4 frames before/after = 9 frames total)
// ±4 is enough - by the 5th frame, movement transitions to the next jump
export const SKIPPING_TEMPORAL_WINDOW = 4;

// Features per frame (9 total) - POSITION-INVARIANT:
// - ankle_y_delta (1) - ankle y relative to window baseline
// - hip_y_delta (1) - hip y relative to window baseline
// - shoulder_y_delta (1) - shoulder y relative to window baseline
// - body_center_y_delta (1) - body center y relative to window baseline
// - ankle_vy (1) - ankle vertical velocity
// - hip_vy (1) - hip vertical velocity
// - body_height (1) - distance from ankles to shoulders
// - left_leg_ratio (1) - knee-ankle / hip-ankle distance (sitting detection)
// - right_leg_ratio (1) - knee-ankle / hip-ankle distance (sitting detection)
const FEATURES_PER_FRAME = 9;

// Indices of position features that should be converted to deltas
const POSITION_FEATURE_INDICES = [0, 1, 2, 3]; // ankle_y, hip_y, shoulder_y, body_center_y

// MoveNet keypoint indices
const KEYPOINT_INDICES = {
  'nose': 0,
  'left_eye': 1,
  'right_eye': 2,
  'left_ear': 3,
  'right_ear': 4,
  'left_shoulder': 5,
  'right_shoulder': 6,
  'left_elbow': 7,
  'right_elbow': 8,
  'left_wrist': 9,
  'right_wrist': 10,
  'left_hip': 11,
  'right_hip': 12,
  'left_knee': 13,
  'right_knee': 14,
  'left_ankle': 15,
  'right_ankle': 16,
};

/**
 * Get a specific keypoint by name
 * @param {Array} keypoints - Array of keypoint objects
 * @param {string} name - Keypoint name (e.g., 'left_ankle')
 * @returns {Object|null} Keypoint with x, y, confidence or null
 */
function getKeypoint(keypoints, name) {
  if (!keypoints || !Array.isArray(keypoints)) return null;

  const idx = KEYPOINT_INDICES[name];
  if (idx === undefined) return null;

  if (idx < keypoints.length) {
    const kp = keypoints[idx];
    if (kp && typeof kp === 'object') {
      // Support both 'score' (MoveNet) and 'confidence' field names
      const confidence = kp.score !== undefined ? kp.score : (kp.confidence || 0);
      return { x: kp.x || 0, y: kp.y || 0, confidence };
    }
  }

  return null;
}

/**
 * Get average position of multiple keypoints
 * @param {Array} keypoints - Array of keypoint objects
 * @param {Array} names - List of keypoint names to average
 * @returns {Object|null} Average {x, y} or null if no valid keypoints
 */
function getAvgPosition(keypoints, names) {
  const validKps = [];

  for (const name of names) {
    const kp = getKeypoint(keypoints, name);
    if (kp && kp.confidence > 0.3) {
      validKps.push(kp);
    }
  }

  if (validKps.length === 0) return null;

  const avgX = validKps.reduce((sum, kp) => sum + kp.x, 0) / validKps.length;
  const avgY = validKps.reduce((sum, kp) => sum + kp.y, 0) / validKps.length;

  return { x: avgX, y: avgY };
}

// Keypoints to interpolate when missing (lower body joints critical for jump detection)
const KEYPOINTS_TO_INTERPOLATE = [
  'left_knee', 'right_knee',
  'left_ankle', 'right_ankle',
  'left_hip', 'right_hip'
];

/**
 * Interpolate missing keypoints using previous and next frames
 *
 * If a keypoint (knee, ankle, hip) is missing or has low confidence in the current frame,
 * but is present in both previous and next frames, interpolate by taking the midpoint.
 *
 * @param {Array} keypoints - Current frame keypoints
 * @param {Array} prevKeypoints - Previous frame keypoints
 * @param {Array} nextKeypoints - Next frame keypoints
 * @returns {Array} Keypoints with interpolated values for missing joints
 */
function interpolateMissingKeypoints(keypoints, prevKeypoints, nextKeypoints) {
  if (!keypoints || !Array.isArray(keypoints)) return keypoints;
  if (!prevKeypoints || !nextKeypoints) return keypoints;

  // Create a copy to avoid mutating original
  const interpolated = keypoints.map(kp => kp ? { ...kp } : kp);

  for (const name of KEYPOINTS_TO_INTERPOLATE) {
    const idx = KEYPOINT_INDICES[name];
    if (idx === undefined) continue;

    const currentKp = getKeypoint(keypoints, name);

    // Check if current keypoint is missing or has low confidence
    if (!currentKp || currentKp.confidence <= 0.3) {
      const prevKp = getKeypoint(prevKeypoints, name);
      const nextKp = getKeypoint(nextKeypoints, name);

      // If both previous and next have good confidence, interpolate
      if (prevKp && nextKp && prevKp.confidence > 0.3 && nextKp.confidence > 0.3) {
        const interpolatedKp = {
          x: (prevKp.x + nextKp.x) / 2,
          y: (prevKp.y + nextKp.y) / 2,
          // Use average confidence of prev/next, marked as interpolated
          score: (prevKp.confidence + nextKp.confidence) / 2
        };

        // Ensure the interpolated array has enough space
        while (interpolated.length <= idx) {
          interpolated.push(null);
        }

        interpolated[idx] = interpolatedKp;
      }
    }
  }

  return interpolated;
}

/**
 * Extract RAW features from a single frame for skipping detection
 *
 * Returns raw normalized positions (not deltas). The delta conversion
 * happens in extractSkippingTemporalFeatures after computing window baseline.
 *
 * Features (7 total):
 * - ankle_y (1) - average y position of both ankles (normalized, will become delta)
 * - hip_y (1) - average y position of both hips (normalized, will become delta)
 * - shoulder_y (1) - average y position of both shoulders (normalized, will become delta)
 * - body_center_y (1) - center of mass approximation (normalized, will become delta)
 * - ankle_vy (1) - ankle vertical velocity (unchanged)
 * - hip_vy (1) - hip vertical velocity (unchanged)
 * - body_height (1) - distance from ankles to shoulders (unchanged)
 *
 * @param {Array} keypoints - Current frame keypoints
 * @param {Array} prevKeypoints - Previous frame keypoints for velocity
 * @param {number} frameW - Frame width for normalization
 * @param {number} frameH - Frame height for normalization
 * @returns {Array} Feature array (9 features, uses -1 for missing data)
 */
export function frameToSkippingFeatures(keypoints, prevKeypoints, frameW, frameH) {
  const features = [];

  // Get average positions for body parts
  const anklePos = getAvgPosition(keypoints, ['left_ankle', 'right_ankle']);
  const hipPos = getAvgPosition(keypoints, ['left_hip', 'right_hip']);
  const shoulderPos = getAvgPosition(keypoints, ['left_shoulder', 'right_shoulder']);

  // Previous frame positions for velocity
  const prevAnklePos = getAvgPosition(prevKeypoints, ['left_ankle', 'right_ankle']);
  const prevHipPos = getAvgPosition(prevKeypoints, ['left_hip', 'right_hip']);

  // Feature 1: Ankle Y (normalized, -1 if missing)
  if (anklePos) {
    features.push(anklePos.y / frameH);
  } else {
    features.push(-1);
  }

  // Feature 2: Hip Y (normalized, -1 if missing)
  if (hipPos) {
    features.push(hipPos.y / frameH);
  } else {
    features.push(-1);
  }

  // Feature 3: Shoulder Y (normalized, -1 if missing)
  if (shoulderPos) {
    features.push(shoulderPos.y / frameH);
  } else {
    features.push(-1);
  }

  // Feature 4: Body center Y (average of ankle, hip, shoulder)
  const validPositions = [anklePos, hipPos, shoulderPos].filter(p => p !== null);
  if (validPositions.length > 0) {
    const centerY = validPositions.reduce((sum, p) => sum + p.y, 0) / validPositions.length;
    features.push(centerY / frameH);
  } else {
    features.push(-1);
  }

  // Feature 5: Ankle vertical velocity
  if (anklePos && prevAnklePos) {
    const ankleVy = (anklePos.y - prevAnklePos.y) / frameH;
    features.push(ankleVy);
  } else {
    features.push(-1);
  }

  // Feature 6: Hip vertical velocity
  if (hipPos && prevHipPos) {
    const hipVy = (hipPos.y - prevHipPos.y) / frameH;
    features.push(hipVy);
  } else {
    features.push(-1);
  }

  // Feature 7: Body height (ankle to shoulder distance, for scale)
  if (anklePos && shoulderPos) {
    const bodyHeight = Math.abs(anklePos.y - shoulderPos.y) / frameH;
    features.push(bodyHeight);
  } else {
    features.push(-1);
  }

  // Get individual keypoints for leg ratio features
  const leftAnkle = getKeypoint(keypoints, 'left_ankle');
  const leftKnee = getKeypoint(keypoints, 'left_knee');
  const leftHip = getKeypoint(keypoints, 'left_hip');
  const rightAnkle = getKeypoint(keypoints, 'right_ankle');
  const rightKnee = getKeypoint(keypoints, 'right_knee');
  const rightHip = getKeypoint(keypoints, 'right_hip');

  // Feature 8: Left leg ratio (knee-ankle distance / hip-ankle distance)
  // Low ratio indicates bent knee (sitting), high ratio indicates straight leg (standing/jumping)
  if (leftAnkle && leftKnee && leftHip && leftAnkle.confidence > 0.3 && leftKnee.confidence > 0.3 && leftHip.confidence > 0.3) {
    const kneeAnkleDist = Math.sqrt(Math.pow(leftKnee.x - leftAnkle.x, 2) + Math.pow(leftKnee.y - leftAnkle.y, 2));
    const hipAnkleDist = Math.sqrt(Math.pow(leftHip.x - leftAnkle.x, 2) + Math.pow(leftHip.y - leftAnkle.y, 2));
    const ratio = hipAnkleDist > 0 ? kneeAnkleDist / hipAnkleDist : -1;
    features.push(ratio);
  } else {
    features.push(-1);
  }

  // Feature 9: Right leg ratio (knee-ankle distance / hip-ankle distance)
  if (rightAnkle && rightKnee && rightHip && rightAnkle.confidence > 0.3 && rightKnee.confidence > 0.3 && rightHip.confidence > 0.3) {
    const kneeAnkleDist = Math.sqrt(Math.pow(rightKnee.x - rightAnkle.x, 2) + Math.pow(rightKnee.y - rightAnkle.y, 2));
    const hipAnkleDist = Math.sqrt(Math.pow(rightHip.x - rightAnkle.x, 2) + Math.pow(rightHip.y - rightAnkle.y, 2));
    const ratio = hipAnkleDist > 0 ? kneeAnkleDist / hipAnkleDist : -1;
    features.push(ratio);
  } else {
    features.push(-1);
  }

  return features;
}

/**
 * Extract temporal features from a frame buffer for skipping detection
 *
 * Total features: (2 * SKIPPING_TEMPORAL_WINDOW + 1) * 9 + 4 = 85 features
 * The +4 are:
 * - accumulated_knees_distance_changes
 * - accumulated_ankles_distance_changes
 * - accumulated_relative_velocity (ankle_vy - shoulder_vy, helps detect camera shake)
 * - ankle_amplitude (peak-to-peak ankle Y range, helps distinguish real jumps from body sway)
 *
 * Position features are normalized to deltas from window baseline to make
 * the model robust to different camera positions.
 *
 * @param {Array} frameBuffer - Array of keypoint arrays
 * @param {number} centerIdx - Index of the center frame in the buffer
 * @param {number} frameW - Frame width for normalization
 * @param {number} frameH - Frame height for normalization
 * @param {Array} prevShiftedKeypoints - Keypoints from the frame shifted out of buffer
 * @returns {Array} Feature array (85 features)
 */
export function extractSkippingTemporalFeatures(frameBuffer, centerIdx, frameW = 640, frameH = 480, prevShiftedKeypoints = null) {
  // Step 1: Collect features for each frame in the window
  const allFrameFeatures = [];

  for (let offset = -SKIPPING_TEMPORAL_WINDOW; offset <= SKIPPING_TEMPORAL_WINDOW; offset++) {
    const idx = centerIdx + offset;
    const prevIdx = idx - 1;
    const nextIdx = idx + 1;

    if (idx >= 0 && idx < frameBuffer.length) {
      let keypoints = frameBuffer[idx];

      // Get previous frame keypoints for velocity
      let prevKeypoints;
      if (prevIdx >= 0) {
        prevKeypoints = frameBuffer[prevIdx];
      } else if (prevIdx === -1 && prevShiftedKeypoints) {
        prevKeypoints = prevShiftedKeypoints;
      } else {
        prevKeypoints = null;
      }

      // Get next frame keypoints for interpolation
      const nextKeypoints = nextIdx < frameBuffer.length ? frameBuffer[nextIdx] : null;

      // Interpolate missing keypoints (knees, ankles, hips) using prev/next frames
      keypoints = interpolateMissingKeypoints(keypoints, prevKeypoints, nextKeypoints);

      const frameFeatures = frameToSkippingFeatures(keypoints, prevKeypoints, frameW, frameH);
      allFrameFeatures.push(frameFeatures);
    } else {
      // Out of bounds - use -1 as sentinel for all features
      const missingFeatures = [];
      for (let i = 0; i < FEATURES_PER_FRAME; i++) {
        missingFeatures.push(-1);
      }
      allFrameFeatures.push(missingFeatures);
    }
  }

  // Step 2: Compute baseline (mean) for each position feature, ignoring -1 values
  const baselines = {};
  for (const featIdx of POSITION_FEATURE_INDICES) {
    const validValues = [];
    for (const frameFeatures of allFrameFeatures) {
      const value = frameFeatures[featIdx];
      if (value !== -1) {
        validValues.push(value);
      }
    }
    if (validValues.length > 0) {
      baselines[featIdx] = validValues.reduce((sum, v) => sum + v, 0) / validValues.length;
    } else {
      baselines[featIdx] = 0; // No valid values, baseline is 0
    }
  }

  // Step 3: Convert position features to deltas and flatten
  const features = [];
  for (const frameFeatures of allFrameFeatures) {
    for (let featIdx = 0; featIdx < FEATURES_PER_FRAME; featIdx++) {
      let value = frameFeatures[featIdx];

      // Convert position features to deltas
      if (POSITION_FEATURE_INDICES.includes(featIdx) && value !== -1) {
        value = value - baselines[featIdx];
      }

      features.push(value);
    }
  }

  // Step 4: Calculate accumulated distance changes for knees and ankles over last 4 frames
  // This helps distinguish real body movement from camera shake
  // If body is still, these values are close to zero
  let accumulatedKneesChange = 0;
  let accumulatedAnklesChange = 0;
  let accumulatedRelativeVelocity = 0;
  let kneesChangeCount = 0;
  let anklesChangeCount = 0;
  let relativeVelocityCount = 0;

  // Look at frames from center-3 to center (last 4 frames including center)
  for (let i = SKIPPING_TEMPORAL_WINDOW - 3; i <= SKIPPING_TEMPORAL_WINDOW; i++) {
    const prevI = i - 1;
    if (prevI < 0 || i >= frameBuffer.length || prevI >= frameBuffer.length) continue;

    const currKeypoints = frameBuffer[centerIdx + (i - SKIPPING_TEMPORAL_WINDOW)];
    const prevKeypoints = frameBuffer[centerIdx + (prevI - SKIPPING_TEMPORAL_WINDOW)];
    if (!currKeypoints || !prevKeypoints) continue;

    // Get knee positions
    const currLeftKnee = getKeypoint(currKeypoints, 'left_knee');
    const currRightKnee = getKeypoint(currKeypoints, 'right_knee');
    const prevLeftKnee = getKeypoint(prevKeypoints, 'left_knee');
    const prevRightKnee = getKeypoint(prevKeypoints, 'right_knee');

    // Get ankle positions
    const currLeftAnkle = getKeypoint(currKeypoints, 'left_ankle');
    const currRightAnkle = getKeypoint(currKeypoints, 'right_ankle');
    const prevLeftAnkle = getKeypoint(prevKeypoints, 'left_ankle');
    const prevRightAnkle = getKeypoint(prevKeypoints, 'right_ankle');

    // Calculate average knee position change
    if (currLeftKnee && currRightKnee && prevLeftKnee && prevRightKnee &&
        currLeftKnee.confidence > 0.3 && currRightKnee.confidence > 0.3 &&
        prevLeftKnee.confidence > 0.3 && prevRightKnee.confidence > 0.3) {
      const currAvgKneeX = (currLeftKnee.x + currRightKnee.x) / 2;
      const currAvgKneeY = (currLeftKnee.y + currRightKnee.y) / 2;
      const prevAvgKneeX = (prevLeftKnee.x + prevRightKnee.x) / 2;
      const prevAvgKneeY = (prevLeftKnee.y + prevRightKnee.y) / 2;
      const kneeDist = Math.sqrt(Math.pow(currAvgKneeX - prevAvgKneeX, 2) + Math.pow(currAvgKneeY - prevAvgKneeY, 2));
      accumulatedKneesChange += kneeDist / frameH; // Normalize by frame height
      kneesChangeCount++;
    }

    // Calculate average ankle position change
    if (currLeftAnkle && currRightAnkle && prevLeftAnkle && prevRightAnkle &&
        currLeftAnkle.confidence > 0.3 && currRightAnkle.confidence > 0.3 &&
        prevLeftAnkle.confidence > 0.3 && prevRightAnkle.confidence > 0.3) {
      const currAvgAnkleX = (currLeftAnkle.x + currRightAnkle.x) / 2;
      const currAvgAnkleY = (currLeftAnkle.y + currRightAnkle.y) / 2;
      const prevAvgAnkleX = (prevLeftAnkle.x + prevRightAnkle.x) / 2;
      const prevAvgAnkleY = (prevLeftAnkle.y + prevRightAnkle.y) / 2;
      const ankleDist = Math.sqrt(Math.pow(currAvgAnkleX - prevAvgAnkleX, 2) + Math.pow(currAvgAnkleY - prevAvgAnkleY, 2));
      accumulatedAnklesChange += ankleDist / frameH; // Normalize by frame height
      anklesChangeCount++;
    }

    // Get shoulder positions for relative velocity calculation
    const currLeftShoulder = getKeypoint(currKeypoints, 'left_shoulder');
    const currRightShoulder = getKeypoint(currKeypoints, 'right_shoulder');
    const prevLeftShoulder = getKeypoint(prevKeypoints, 'left_shoulder');
    const prevRightShoulder = getKeypoint(prevKeypoints, 'right_shoulder');

    // Calculate relative velocity: ankle_vy - shoulder_vy for left side
    // In real jumps, ankles move more than shoulders (higher relative velocity)
    // In camera shake, both move together (relative velocity near zero)
    if (currLeftAnkle && prevLeftAnkle && currLeftShoulder && prevLeftShoulder &&
        currLeftAnkle.confidence > 0.3 && prevLeftAnkle.confidence > 0.3 &&
        currLeftShoulder.confidence > 0.3 && prevLeftShoulder.confidence > 0.3) {
      const leftAnkleVy = (currLeftAnkle.y - prevLeftAnkle.y) / frameH;
      const leftShoulderVy = (currLeftShoulder.y - prevLeftShoulder.y) / frameH;
      accumulatedRelativeVelocity += Math.abs(leftAnkleVy - leftShoulderVy);
      relativeVelocityCount++;
    }

    // Calculate relative velocity for right side
    if (currRightAnkle && prevRightAnkle && currRightShoulder && prevRightShoulder &&
        currRightAnkle.confidence > 0.3 && prevRightAnkle.confidence > 0.3 &&
        currRightShoulder.confidence > 0.3 && prevRightShoulder.confidence > 0.3) {
      const rightAnkleVy = (currRightAnkle.y - prevRightAnkle.y) / frameH;
      const rightShoulderVy = (currRightShoulder.y - prevRightShoulder.y) / frameH;
      accumulatedRelativeVelocity += Math.abs(rightAnkleVy - rightShoulderVy);
      relativeVelocityCount++;
    }
  }

  // Add accumulated change features (-1 if not enough data)
  features.push(kneesChangeCount >= 2 ? accumulatedKneesChange : -1);
  features.push(anklesChangeCount >= 2 ? accumulatedAnklesChange : -1);
  features.push(relativeVelocityCount >= 2 ? accumulatedRelativeVelocity : -1);

  // Step 5: Calculate ankle amplitude (peak-to-peak Y range across full window)
  // This helps distinguish real jumps (larger amplitude) from small body sway
  const ankleYPositions = [];
  for (let offset = -SKIPPING_TEMPORAL_WINDOW; offset <= SKIPPING_TEMPORAL_WINDOW; offset++) {
    const idx = centerIdx + offset;
    if (idx >= 0 && idx < frameBuffer.length) {
      const keypoints = frameBuffer[idx];
      const leftAnkle = getKeypoint(keypoints, 'left_ankle');
      const rightAnkle = getKeypoint(keypoints, 'right_ankle');

      if (leftAnkle && rightAnkle && leftAnkle.confidence > 0.3 && rightAnkle.confidence > 0.3) {
        const avgAnkleY = (leftAnkle.y + rightAnkle.y) / 2 / frameH;
        ankleYPositions.push(avgAnkleY);
      }
    }
  }

  // Ankle amplitude: peak-to-peak range (requires at least 5 valid positions)
  let ankleAmplitude = -1;
  if (ankleYPositions.length >= 5) {
    ankleAmplitude = Math.max(...ankleYPositions) - Math.min(...ankleYPositions);
  }
  features.push(ankleAmplitude);

  return features;
}

/**
 * Create a skipping XGBoost predictor
 * @returns {XGBoostPredictor} A new predictor instance
 */
export function createSkippingPredictor() {
  return new XGBoostPredictor();
}
